import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  WATCHED_PERCENT,
  clockLabel,
  episodePercent,
  isKnownErrorCode,
  resumePoint,
  resumePreview,
  watchStateOf,
  type EpisodeInfo,
  type ErrorCode,
  type MovieDetail,
  type SeriesDetail,
  type TitleFacts,
  type VodItem,
  type VodShelfItem,
  type WatchProgress,
} from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { Translator } from '../i18n/i18n.js';
import { PosterTile } from '../components/PosterTile.js';
import { InfoPanel, durationLabel } from './InfoPanel.js';
import { scrollFocusedIntoView } from '../scrollFocusedIntoView.js';

/**
 * La scheda di un film o di una serie (03-SCHERMATE-TV §7), nella forma del prototipo v3.
 *
 * A sinistra il titolo a 78 px con genere, anno e trama; a destra la key art 16:9. Sotto, per una
 * serie, le pastiglie delle stagioni e la fascia degli episodi; in fondo «Potrebbe piacerti anche».
 *
 * ## Il tasto principale dice cosa farà, non cosa è
 *
 * «Riprendi S2 E3» e «Guarda» non sono lo stesso tasto con due etichette: sono due azioni, e quale
 * sia lo decide l'avanzamento. Un tasto che dice «Riprendi» e riparte da zero è il modo più veloce di
 * far perdere fiducia a chi guarda — e «Dall'inizio» esiste **solo** quando c'è un punto da cui si
 * riprenderebbe, altrimenti sarebbe un secondo tasto identico al primo.
 *
 * ## Le stagioni si contano, gli episodi si chiedono
 *
 * Il listino porta un titolo per serie e non sa quante stagioni abbia: `detail` arriva da una
 * chiamata a sé, e finché non è arrivata la scheda mostra quel che il listino sa — titolo, genere,
 * anno, locandina — invece di una schermata ferma. È la stessa regola della diretta: mai uno schermo
 * vuoto senza una parola sopra.
 *
 * ## «La mia lista» non c'è
 *
 * Il prototipo ha un quarto tasto che aggiunge alla propria lista. Non è qui perché quella lista vive
 * su `line.app_data`, cioè sul gateway, e in questa versione i dati di chi guarda stanno
 * sull'apparecchio (vedi i preferiti): una lista che non si sincronizza e che si chiama «la mia
 * lista» prometterebbe fra apparecchi qualcosa che non avviene. Il tasto arriva quando arriva la
 * sincronia, non prima.
 */

export interface VodDetailScreenProps {
  readonly focusPort: FocusPort;
  readonly item: VodItem;
  /** La scheda con le stagioni, o `null` finché non è arrivata (o per un film, che non ne ha). */
  readonly detail: SeriesDetail | null;
  /**
   * La scheda di un film: trama, voto, durata, frase di lancio, immagine larga.
   *
   * Per una serie resta `null` — quei fatti li porta `detail`, che è già un `TitleFacts`. Due prop e
   * non una perché sono due chiamate diverse (`get_vod_info` e `get_series_info`) e arrivano in
   * momenti diversi: fonderle qui vorrebbe dire un tipo unione che la schermata deve smontare a ogni
   * riga.
   */
  readonly movie?: MovieDetail | null;
  readonly progress: WatchProgress;
  readonly similar: ReadonlyArray<VodShelfItem>;
  /** Riproduce un film, o un episodio. Chi sta sopra sa comporre l'indirizzo. */
  readonly onPlayMovie: (fromStart: boolean) => void;
  readonly onPlayEpisode: (episode: EpisodeInfo, fromStart: boolean) => void;
  /**
   * Segna visto o da vedere: un film intero, oppure **una stagione**.
   *
   * `season` è `null` per un film e il numero della stagione mostrata per una serie. Senza il
   * parametro, l'etichetta diceva «Segna S2 come vista» e l'azione segnava l'intera serie: una
   * promessa e un'altra cosa, e senza modo di tornare indietro — il segno non ricorda cosa c'era
   * prima di lui.
   */
  readonly onToggleWatched: (season: number | null) => void;
  /**
   * «La mia lista», su **questo apparecchio**.
   *
   * Nel prototipo la lista vive su `line.app_data`, cioè sul gateway. Qui sta in locale come i
   * preferiti e per la stessa ragione: la sincronia fra apparecchi è una funzione a sé, con i suoi
   * conflitti, e non è in questo giro. Chiamarla «la mia lista» e non sincronizzarla prometterebbe
   * fra apparecchi qualcosa che non avviene — quindi finché resta locale, resta locale e basta.
   */
  readonly onToggleMyList?: () => void;
  readonly inMyList?: boolean;
  readonly onOpen: (contentId: string) => void;
  readonly onExit: () => void;
  /** Vero mentre la scheda della serie sta arrivando. */
  readonly loadingDetail?: boolean;
  /**
   * Perché la scheda non è arrivata, o `null` se è arrivata.
   *
   * Senza questa prop la schermata **taceva**: chi sta sopra prendeva l'errore, lasciava `detail` a
   * `null`, e qui il blocco delle stagioni semplicemente non veniva disegnato. Sullo schermo restavano
   * titolo, genere e anno — una serie che sembrava non avere episodi — e nessun modo di distinguerlo
   * da un gateway irraggiungibile. È il modo peggiore di fallire, perché somiglia a un dato.
   */
  readonly detailErrorCode?: ErrorCode | string | null;
}

/** Un elemento raggiungibile col telecomando, con cosa fa se lo si preme. */
interface Focusable {
  readonly id: string;
  readonly run: () => void;
}

export function VodDetailScreen({
  focusPort,
  item,
  detail,
  movie = null,
  progress,
  similar,
  onPlayMovie,
  onPlayEpisode,
  onToggleWatched,
  onToggleMyList,
  inMyList = false,
  onOpen,
  onExit,
  loadingDetail = false,
  detailErrorCode = null,
}: VodDetailScreenProps): ReactNode {
  const translator = useTranslator();
  const [season, setSeason] = useState<number | null>(null);
  /** Il pannello Info è aperto: finché lo è, i tasti sono suoi e non di questa schermata. */
  const [infoOpen, setInfoOpen] = useState(false);
  const [cursor, setCursor] = useState<{ row: number; col: number }>({ row: 0, col: 0 });

  const handlers = useRef({ onPlayMovie, onPlayEpisode, onToggleWatched, onToggleMyList, onOpen, onExit });
  useEffect(() => {
    handlers.current = { onPlayMovie, onPlayEpisode, onToggleWatched, onToggleMyList, onOpen, onExit };
  }, [onPlayMovie, onPlayEpisode, onToggleWatched, onToggleMyList, onOpen, onExit]);

  /*
    Quale stagione si vede: **quella da cui si riprenderebbe**, non la prima.

    Aprire la scheda di una serie guardata fino alla terza stagione e trovare la prima significa
    cercare a mano dove si era arrivati, ogni volta. Resta `null` finché la scheda non arriva, così il
    valore scelto è quello giusto e non uno da correggere subito dopo.
  */
  const resume = useMemo(() => (detail === null ? null : resumePoint(detail, progress)), [detail, progress]);
  useEffect(() => {
    if (detail === null) return;
    setSeason((current) => {
      if (current !== null && detail.seasons.some((entry) => entry.number === current)) return current;
      return resume?.season ?? detail.seasons[0]?.number ?? null;
    });
  }, [detail, resume]);

  const episodes = useMemo(
    () => detail?.seasons.find((entry) => entry.number === season)?.episodes ?? [],
    [detail, season],
  );

  /*
    Lo stato di visione del titolo, con le stagioni dentro.

    Per una serie `watchStateOf` conta gli episodi da `episodesPerSeason`, che il listino lascia
    vuoto: senza innestare le stagioni la scheda di una serie guardata a metà direbbe «mai iniziata»,
    cioè annuncerebbe a chi guarda che il suo avanzamento è andato perso.
  */
  const state = useMemo(() => {
    if (item.kind === 'movie' || detail === null) return watchStateOf(item, progress);
    return watchStateOf(
      { ...item, episodesPerSeason: detail.seasons.map((entry) => entry.episodes.length) },
      progress,
    );
  }, [item, detail, progress]);

  /*
    Se la **stagione mostrata** è tutta vista: è quel che decide l'etichetta del tasto.

    `state.watched` parla della serie intera, e su una serie da quattro stagioni con la seconda finita
    direbbe «Segna S2 come vista» quando S2 è già vista — cioè il contrario di quel che farebbe.
  */
  const seasonWatched = useMemo(() => {
    if (item.kind !== 'series' || detail === null || season === null) return state.watched;
    const chosen = detail.seasons.find((entry) => entry.number === season);
    if (!chosen || chosen.episodes.length === 0) return false;
    return chosen.episodes.every(
      (entry) => episodePercent(progress, item.id, entry) >= WATCHED_PERCENT,
    );
  }, [item, detail, season, progress, state.watched]);

  /** Le righe di elementi premibili, nell'ordine in cui stanno sullo schermo. */
  const rows: ReadonlyArray<ReadonlyArray<Focusable>> = useMemo(() => {
    const actions: Focusable[] = [];
    /*
      I tasti del disegno, nell'ordine: primario, **Dall'inizio**, segna, La mia lista.

      «Dall'inizio» c'è **sempre**, e non solo quando c'è qualcosa da riprendere. Lo avevo reso
      condizionale ragionando che su un film mai aperto sarebbe stato un doppione del primario — ed è
      vero per i film, ma non per le serie, dove il primario apre l'episodio da riprendere e questo
      riparte da S1 E1. Un tasto che compare e scompare a seconda dello stato costringe a cercarlo
      ogni volta; il disegno lo tiene fermo, e ha ragione.
    */
    if (item.kind === 'movie') {
      actions.push({ id: 'play', run: () => handlers.current.onPlayMovie(false) });
      actions.push({ id: 'restart', run: () => handlers.current.onPlayMovie(true) });
    } else if (resume !== null) {
      actions.push({ id: 'play', run: () => handlers.current.onPlayEpisode(resume, false) });
      const first = detail?.seasons[0]?.episodes[0] ?? null;
      if (first !== null) {
        actions.push({ id: 'restart', run: () => handlers.current.onPlayEpisode(first, true) });
      }
    }
    actions.push({
      id: 'watched',
      run: () => handlers.current.onToggleWatched(item.kind === 'series' ? season : null),
    });
    if (onToggleMyList !== undefined) {
      actions.push({ id: 'myList', run: () => handlers.current.onToggleMyList?.() });
    }
    actions.push({ id: 'info', run: () => setInfoOpen(true) });

    const seasonRow: Focusable[] = (detail?.seasons ?? []).map((entry) => ({
      id: `season-${entry.number}`,
      run: () => setSeason(entry.number),
    }));
    const episodeRow: Focusable[] = episodes.map((entry) => ({
      id: `episode-${entry.id}`,
      run: () => handlers.current.onPlayEpisode(entry, false),
    }));
    const similarRow: Focusable[] = similar.map((entry) => ({
      id: `similar-${entry.item.id}`,
      run: () => handlers.current.onOpen(entry.item.id),
    }));

    // Le righe vuote si tolgono **qui**, non si saltano navigando: un indice che non corrisponde a
    // niente sullo schermo è la premessa di un fuoco che sparisce.
    return [actions, seasonRow, episodeRow, similarRow].filter((row) => row.length > 0);
  }, [item, state.percent, resume, progress, detail, episodes, similar, onToggleMyList, season]);

  const move = useCallback(
    (deltaRow: number, deltaCol: number) => {
      setCursor((current) => {
        if (deltaRow !== 0) {
          const row = current.row + deltaRow;
          if (row < 0 || row >= rows.length) return current;
          return { row, col: Math.min(current.col, (rows[row]?.length ?? 1) - 1) };
        }
        const col = current.col + deltaCol;
        if (col < 0 || col >= (rows[current.row]?.length ?? 0)) return current;
        return { row: current.row, col };
      });
    },
    [rows],
  );

  useEffect(() => {
    // Con il pannello aperto **non ci si iscrive affatto**: due iscrizioni attive ricevono la stessa
    // freccia, e si tornerebbe indietro con un titolo diverso da quello che si era aperto.
    if (infoOpen) return undefined;
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onExit();
        return true;
      }
      if (key.kind === 'select') {
        rows[cursor.row]?.[cursor.col]?.run();
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'left') move(0, -1);
      if (key.direction === 'right') move(0, 1);
      if (key.direction === 'up') move(-1, 0);
      if (key.direction === 'down') move(1, 0);
      return true;
    });
  }, [focusPort, rows, cursor, move, infoOpen]);

  /*
    Il cursore si riporta dentro i limiti quando le righe cambiano.

    Cambiando stagione la fascia degli episodi si accorcia, e un cursore rimasto sull'episodio
    quattordici di una stagione da sei punterebbe a niente: premere OK non farebbe nulla, che è il
    modo peggiore di rompersi — sembra un telecomando scarico.
  */
  useEffect(() => {
    setCursor((current) => {
      const row = Math.min(current.row, rows.length - 1);
      const columns = rows[row]?.length ?? 1;
      if (row === current.row && current.col < columns) return current;
      return { row: Math.max(0, row), col: Math.min(current.col, columns - 1) };
    });
  }, [rows]);

  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollFocusedIntoView(frameRef.current?.querySelector<HTMLElement>('[data-focused="1"]'));
  }, [cursor, rows]);

  const focusedId = rows[cursor.row]?.[cursor.col]?.id ?? null;
  /*
    I fatti del titolo, da qualunque delle due schede sia arrivata.

    `SeriesDetail` **estende** `TitleFacts`, quindi per una serie la scheda è già quel che serve. È la
    ragione per cui i due tipi condividono un antenato invece di avere due elenchi di campi paralleli
    che al primo campo nuovo si scostano.
  */
  const facts: TitleFacts | null = detail ?? movie;

  /*
    L'immagine della copertina: la **larga** quando c'è, la locandina come ripiego.

    Il riquadro dietro il titolo è 16:9 e una locandina 2:3 ritagliata dentro perde metà
    dell'inquadratura. `backdrop` è il campo che il backend aveva in tabella e non mandava: senza,
    restava il tratteggio del prototipo.
  */
  const coverUrl = facts?.backdropUrl ?? item.posterUrl;

  /**
   * L'immagine dell'anteprima di ripresa: **la locandina, e in quest'ordine**.
   *
   * Due immagini e due forme, quindi due variabili. Qui serve un 2:3 perché il riquadro adesso è 2:3
   * (`layout.resumePreview*`); prima era 16:9 e condivideva `coverUrl` con lo sfondo, così su una
   * serie senza `backdrop` ci finiva la locandina tagliata a striscia — è lo screen del 30-09-2026,
   * col titolo mozzato a metà.
   *
   * Il `backdrop` resta in coda e non sparisce: è un 16:9 dentro un 2:3, quindi viene tagliato ai
   * lati — ma è un ritaglio che si legge come un'inquadratura stretta, non come un'immagine rotta, e
   * l'alternativa è il tratteggio vuoto. La locandina, quando c'è, viene sempre prima.
   */
  const previewUrl = facts?.posterUrl ?? item.posterUrl ?? facts?.backdropUrl ?? null;

  // Il titolo della scheda quando c'è, quello del listino altrimenti: sono lo stesso titolo, e la
  // scheda arriva dopo. Preferire il vuoto della scheda lascerebbe l'intestazione senza nome.
  const cardTitle = detail?.title ?? movie?.title ?? '';
  const title = cardTitle !== '' ? cardTitle : item.title;
  const plot = facts?.plot ?? '';

  /*
    Cosa dice l'anteprima di ripresa. Le tre decisioni — etichetta, barra, tempi — stanno nel
    dominio (`resumePreview`), perché sono conti che si sbagliano in silenzio.

    Per una serie il punto di ripresa è **l'episodio** da cui si riprenderebbe, non la serie: la riga
    sotto dice «S2 E3 · riprende da» e i tempi sono quelli di quella puntata.
  */
  const resumeEpisode = item.kind === 'series' ? resume : null;
  const resumePercentValue =
    item.kind === 'movie'
      ? progress.movies[item.id] ?? 0
      : resumeEpisode === null
        ? 0
        : episodePercent(progress, item.id, resumeEpisode);
  const resumeDurationMin =
    item.kind === 'movie' ? facts?.durationMin ?? null : resumeEpisode?.durationMin ?? null;
  const preview = resumePreview(resumePercentValue, resumeDurationMin);

  const resumeSubject =
    resumeEpisode === null
      ? title
      : `S${resumeEpisode.season} E${resumeEpisode.episode}`;
  const resumeLine = preview.resuming
    ? translator.t('vod.resume.from', { subject: resumeSubject })
    : resumeSubject;
  /*
    I tempi si scrivono **solo se la durata si conosce**.

    Su un catalogo importato la durata manca a macchia, e un «1:30:00» inventato è peggio di niente:
    nessuno mette in dubbio un tempo scritto accanto a una barra.
  */
  const resumeTime =
    preview.durationSec === 0
      ? null
      : preview.resuming
        ? `${clockLabel(preview.positionSec)} / ${clockLabel(preview.durationSec)}`
        : clockLabel(preview.durationSec);
  // Il titolo della scheda quando c'è, quello del listino altrimenti: sono lo stesso titolo, e la
  // scheda arriva dopo. Preferire il vuoto della scheda lascerebbe l'intestazione senza nome.

  return (
    <div
      ref={frameRef}
      data-vod-detail={item.id}
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '64px 72px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        boxSizing: 'border-box',
      }}
    >
      {/*
        La copertina **a tutta larghezza**, dietro il titolo.

        Nel disegno il blocco in alto esce dai margini della schermata (`margin:-64px -72px 0`) e si
        riprende il respiro come `padding`: l'immagine tocca i bordi, il testo no. Era una card di
        640 × 360 in un angolo — cioè la stessa immagine ridotta a illustrazione, mentre qui è lo
        sfondo su cui si legge tutto il resto.

        Tre strati: l'immagine, il **velo** che rende leggibile il testo bianco, e la sfumatura che
        chiude verso il fondo della pagina. I secondi due sono variabili CSS perché l'impostazione
        «Effetti visivi» li deve spegnere davvero (07-PRESTAZIONI §5).
      */}
      <div
        data-vod-cover="1"
        style={{
          position: 'relative',
          margin: '-64px -72px 0',
          padding: '120px 72px 56px',
          minHeight: 680,
          display: 'flex',
          gap: 56,
          alignItems: 'flex-end',
          boxSizing: 'border-box',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `repeating-linear-gradient(135deg,${colors.surfaceRaised} 0 18px,${colors.surfaceProgram} 18px 36px)`,
            overflow: 'hidden',
          }}
        >
          {coverUrl !== null && (
            <img
              src={coverUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              decoding="async"
            />
          )}
        </div>
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'var(--ov, rgba(10,13,19,.82))' }} />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 220,
            background: 'var(--fade, linear-gradient(0deg,#0A0D13,rgba(10,13,19,0)))',
          }}
        />

        <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span
            style={{
              font: `700 22px ${FONT_STACK}`,
              color: colors.accent,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
            }}
          >
            {translator.t(item.kind === 'movie' ? 'vod.kind.movie' : 'vod.kind.series')}
          </span>
          <h1 style={{ font: `800 78px/.98 ${FONT_STACK}`, letterSpacing: '-.03em', margin: 0 }}>{title}</h1>
          <span style={{ font: `500 25px ${FONT_STACK}`, color: colors.textMuted }}>
            {metaLine(item, facts, detail, translator)}
          </span>
          {/*
            Il voto e la frase di lancio, che stavano in tabella e non uscivano da nessuna parte.

            Il voto è la seconda cosa che si guarda su una scheda, dopo il titolo. Il numero dei voti
            gli sta accanto perché un 8,5 su nove persone e un 8,5 su novemila non sono lo stesso 8,5 —
            e senza il secondo numero non c'è modo di distinguerli.
          */}
          {facts !== null && facts.rating !== null && (
            <span data-vod-rating="1" style={{ font: `700 26px ${FONT_STACK}`, color: colors.accent }}>
              ★ {facts.rating.toFixed(1)}
              {facts.voteCount !== null && (
                <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textFaint, paddingLeft: 10 }}>
                  {translator.t('vod.votes', { count: facts.voteCount })}
                </span>
              )}
            </span>
          )}
          {facts !== null && facts.tagline !== '' && (
            <span
              data-vod-tagline="1"
              style={{ font: `500 24px/1.3 ${FONT_STACK}`, color: colors.textDim, fontStyle: 'italic' }}
            >
              {facts.tagline}
            </span>
          )}
          <span data-watch-status="1" style={{ font: `700 24px ${FONT_STACK}`, color: statusColor(state) }}>
            {statusLine(state, translator)}
          </span>
          {plot !== '' ? (
            <p style={{ font: `400 26px/1.45 ${FONT_STACK}`, color: colors.textMuted, maxWidth: 760, margin: 0 }}>
              {plot}
            </p>
          ) : loadingDetail ? (
            <p style={{ font: `400 26px/1.45 ${FONT_STACK}`, color: colors.textFaint, margin: 0 }}>
              {translator.t('vod.detail.loading')}
            </p>
          ) : null}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, paddingTop: 10 }}>
            {(rows[0] ?? []).map((action) => (
              <button
                key={action.id}
                type="button"
                data-f="1"
                data-action={action.id}
                data-focused={focusedId === action.id ? '1' : '0'}
                onClick={action.run}
                style={{
                  height: 72,
                  padding: '0 32px',
                  border: 0,
                  borderRadius: 14,
                  cursor: 'pointer',
                  // Il primario è chiaro su scuro: uno solo, e si vede da tre metri quale sia.
                  background: action.id === 'play' ? colors.text : colors.surfaceButton,
                  color: action.id === 'play' ? colors.background : colors.text,
                  font: `${action.id === 'play' ? 700 : 600} 26px ${FONT_STACK}`,
                  outline: focusedId === action.id ? `4px solid ${colors.accent}` : 'none',
                  outlineOffset: 4,
                  transform: focusedId === action.id ? 'scale(var(--fs, 1.04))' : 'none',
                  transition: 'var(--tr, transform .16s ease)',
                }}
              >
                {actionLabel(action.id, item, resume, state, translator, { inMyList, season, seasonWatched })}
              </button>
            ))}
          </div>
        </div>

        {/*
          **L'anteprima di ripresa**: il fotogramma del punto in cui si era arrivati, e un tasto.

          Non è un'illustrazione — è il pulsante principale disegnato in grande: premerla fa quel che
          fa il tasto primario, e nel frattempo dice **dove** si riprenderebbe. Era la parte della
          scheda che mancava del tutto.
        */}
        <button
          type="button"
          data-f="1"
          data-resume-preview={preview.badge}
          data-focused={focusedId === 'play' ? '1' : '0'}
          onClick={() => rows[0]?.find((action) => action.id === 'play')?.run()}
          style={{
            position: 'relative',
            flex: 'none',
            width: layout.resumePreviewWidth,
            border: 0,
            margin: 0,
            padding: 0,
            borderRadius: 18,
            overflow: 'hidden',
            background: colors.playerBackground,
            color: colors.text,
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            outline: focusedId === 'play' ? `4px solid ${colors.accent}` : 'none',
            outlineOffset: 4,
            transform: focusedId === 'play' ? 'scale(var(--fs, 1.04))' : 'none',
            transition: 'var(--tr, transform .16s ease)',
          }}
        >
          <div
            data-resume-cover="1"
            style={{
              position: 'relative',
              height: layout.resumePreviewHeight,
              background: `repeating-linear-gradient(135deg,#141A24 0 14px,#10151E 14px 28px)`,
              overflow: 'hidden',
            }}
          >
            {previewUrl !== null && (
              <img
                src={previewUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                decoding="async"
              />
            )}
            <span
              data-resume-badge="1"
              style={{
                position: 'absolute',
                left: 16,
                top: 16,
                padding: '6px 12px',
                borderRadius: 8,
                background: 'rgba(5,7,10,.8)',
                font: `800 16px ${FONT_STACK}`,
                letterSpacing: '.08em',
              }}
            >
              {translator.t(`vod.resume.${preview.badge}`)}
            </span>
            {/*
              Il tondo con il triangolo del prototipo: due bordi CSS, nessuna immagine da caricare.

              88 e non 104: su una casella larga 300 invece di 560, il tondo del prototipo coprirebbe
              un terzo della locandina — cioè proprio quel che questa casella esiste per far vedere.
              Resta abbondantemente leggibile da tre metri.
            */}
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 88,
                height: 88,
                margin: '-44px 0 0 -44px',
                borderRadius: '50%',
                background: 'rgba(242,244,247,.94)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  marginLeft: 7,
                  borderLeft: `29px solid ${colors.background}`,
                  borderTop: '18px solid transparent',
                  borderBottom: '18px solid transparent',
                }}
              />
            </span>
            {preview.percent > 0 && (
              <div
                style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 6, background: 'rgba(255,255,255,.18)' }}
              >
                <div
                  data-resume-progress="1"
                  style={{ height: '100%', width: `${preview.percent}%`, background: colors.accent }}
                />
              </div>
            )}
          </div>
          <span
            style={{
              padding: '16px 20px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              background: colors.surface,
            }}
          >
            <span
              data-resume-line="1"
              style={{ font: `700 22px ${FONT_STACK}`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {resumeLine}
            </span>
            {resumeTime !== null && (
              <span
                data-resume-time="1"
                style={{ flex: 'none', font: '600 20px ui-monospace, Menlo, monospace', color: colors.textMuted }}
              >
                {resumeTime}
              </span>
            )}
          </span>
        </button>
      </div>

      {/*
        Perché le stagioni non ci sono, quando non ci sono.

        Tre casi e tre frasi diverse, perché per chi guarda sono tre problemi diversi: la scheda non è
        arrivata (e allora si dice **perché**, col messaggio dell'errore), la scheda è arrivata e il
        catalogo non ha episodi per questa serie, oppure sta ancora arrivando. Prima erano tutti e tre
        lo stesso schermo vuoto.
      */}
      {item.kind === 'series' && (detail === null || detail.seasons.length === 0) && !loadingDetail && (
        <div
          data-seasons-missing={detailErrorCode !== null ? 'failed' : 'empty'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '18px 22px',
            borderRadius: 14,
            background: colors.surfaceRaised,
            maxWidth: 760,
          }}
        >
          <span style={{ font: `700 24px ${FONT_STACK}`, color: colors.text }}>
            {translator.t(detailErrorCode !== null ? 'vod.detail.failed' : 'vod.detail.noEpisodes')}
          </span>
          {detailErrorCode !== null && (
            <span style={{ font: `400 22px/1.4 ${FONT_STACK}`, color: colors.textMuted }}>
              {/*
                Il codice tradotto se lo conosciamo, il codice **nudo** se no.

                Un gateway può mandare un codice che questa versione dell'app non ha mai visto, e
                `translator.t` risponde con la chiave: chi guarda leggerebbe `errors.foo.bar`, che
                somiglia a un guasto dell'app invece che a una risposta del server. Il codice grezzo
                almeno si può riferire a chi assiste.
              */}
              {isKnownErrorCode(detailErrorCode)
                ? translator.t(`errors.${detailErrorCode}`)
                : detailErrorCode}
            </span>
          )}
        </div>
      )}

      {detail !== null && detail.seasons.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 12, padding: '8px 0', flexWrap: 'wrap' }}>
            {detail.seasons.map((entry) => {
              const active = entry.number === season;
              const id = `season-${entry.number}`;
              return (
                <button
                  key={entry.number}
                  type="button"
                  data-f="1"
                  data-season={entry.number}
                  data-active={active ? '1' : '0'}
                  data-focused={focusedId === id ? '1' : '0'}
                  onClick={() => setSeason(entry.number)}
                  style={{
                    height: 60,
                    padding: '0 26px',
                    border: 0,
                    borderRadius: 30,
                    cursor: 'pointer',
                    background: active ? colors.text : colors.surfaceRaised,
                    color: active ? colors.background : colors.textMuted,
                    font: `600 23px ${FONT_STACK}`,
                    outline: focusedId === id ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 4,
                    transition: 'var(--tr, transform .16s ease)',
                  }}
                >
                  {translator.t('vod.season', { number: entry.number })}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 24, overflowX: 'auto', scrollbarWidth: 'none', padding: '14px 8px 18px', margin: '0 -8px' }}>
            {episodes.map((entry) => {
              const percent = episodePercent(progress, item.id, entry);
              return (
                <PosterTile
                  key={entry.id}
                  variant="episode"
                  title={translator.t('vod.episode', { number: entry.episode, title: entry.title })}
                  subtitle={episodeSubtitle(entry, translator)}
                  posterUrl={entry.imageUrl}
                  percent={percent > 0 && percent < WATCHED_PERCENT ? percent : null}
                  watched={percent >= WATCHED_PERCENT}
                  lockedRating={null}
                  focused={focusedId === `episode-${entry.id}`}
                  onSelect={() => handlers.current.onPlayEpisode(entry, false)}
                />
              );
            })}
          </div>
        </div>
      )}

      {infoOpen && (
        <InfoPanel
          focusPort={focusPort}
          item={item}
          facts={facts}
          state={state}
          primaryLabel={actionLabel('play', item, resume, state, translator, { inMyList, season, seasonWatched })}
          onPrimary={() => {
            setInfoOpen(false);
            rows[0]?.find((action) => action.id === 'play')?.run();
          }}
          onToggleMyList={onToggleMyList}
          inMyList={inMyList}
          onClose={() => setInfoOpen(false)}
        />
      )}

      {similar.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ font: `700 32px ${FONT_STACK}` }}>{translator.t('vod.similar')}</span>
          <div style={{ display: 'flex', gap: 24, overflowX: 'auto', scrollbarWidth: 'none', padding: '14px 8px 18px', margin: '0 -8px' }}>
            {similar.map((entry) => (
              <PosterTile
                key={entry.item.id}
                title={entry.item.title}
                subtitle={entry.item.year > 0 ? String(entry.item.year) : entry.item.genre}
                posterUrl={entry.item.posterUrl}
                percent={entry.state.percent}
                watched={entry.state.watched}
                lockedRating={null}
                focused={focusedId === `similar-${entry.item.id}`}
                onSelect={() => handlers.current.onOpen(entry.item.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Verde se visto, ambra se in corso, **grigio se mai iniziato**.
 *
 * Erano due colori: tutto quel che non era visto usciva in ambra, cioè con lo stesso peso di un
 * titolo lasciato a metà. Il disegno ne usa tre (`statusFg`), e la differenza è quella fra «hai
 * qualcosa da riprendere» e «non l'hai mai aperto» — che è l'informazione per cui quella riga esiste.
 */
function statusColor(state: ReturnType<typeof watchStateOf>): string {
  if (state.watched) return colors.success;
  return state.started ? colors.accent : colors.textDim;
}

function statusLine(state: ReturnType<typeof watchStateOf>, translator: Translator): string {
  if (state.watched) return translator.t('vod.status.watched');
  if (state.totalEpisodes > 0 && state.doneEpisodes > 0) {
    return translator.t('vod.status.episodes', {
      done: state.doneEpisodes,
      total: state.totalEpisodes,
    });
  }
  if (state.percent > 0) return translator.t('vod.status.percent', { percent: state.percent });
  return translator.t('vod.status.new');
}

/**
 * Tipo, genere, anno, durata e — per una serie con la scheda arrivata — il numero di stagioni.
 *
 * Ogni parte c'è **solo se c'è il dato**: una riga con i separatori e i buchi in mezzo («Film ·  ·
 * 2024») somiglia a una risposta arrivata male, e su un catalogo importato i buchi sono la regola.
 */
function metaLine(
  item: VodItem,
  facts: TitleFacts | null,
  detail: SeriesDetail | null,
  translator: Translator,
): string {
  const parts: string[] = [translator.t(item.kind === 'movie' ? 'vod.kind.movie' : 'vod.kind.series')];
  const genre = facts?.genre !== undefined && facts.genre !== '' ? facts.genre : item.genre;
  if (genre !== '') parts.push(genre);
  const year = facts?.year !== undefined && facts.year > 0 ? facts.year : item.year;
  if (year > 0) parts.push(String(year));
  if (facts?.durationMin != null && facts.durationMin > 0) {
    parts.push(durationLabel(facts.durationMin, translator));
  }
  if (detail !== null && detail.seasons.length > 0) {
    parts.push(translator.t('vod.seasons', { count: detail.seasons.length }));
  }
  return parts.join(' · ');
}

function actionLabel(
  id: string,
  item: VodItem,
  resume: EpisodeInfo | null,
  state: ReturnType<typeof watchStateOf>,
  translator: Translator,
  options: {
    readonly inMyList: boolean;
    readonly season: number | null;
    /** Se la **stagione mostrata** è tutta vista: per una serie è questo a decidere, non la serie. */
    readonly seasonWatched: boolean;
  },
): string {
  if (id === 'restart') return translator.t('vod.action.fromStart');
  if (id === 'info') return translator.t('vod.action.info');
  if (id === 'myList') {
    return translator.t(options.inMyList ? 'vod.action.inMyList' : 'vod.action.addMyList');
  }
  if (id === 'watched') {
    /*
      Su una serie si segna **la stagione che si sta guardando**, non la serie intera.

      È quel che fa il disegno (`markSeason`), e la differenza pesa: un tasto premuto per sbaglio che
      segna quaranta puntate non si torna indietro, perché il segno non ricorda cosa c'era prima.
    */
    if (item.kind === 'series' && options.season !== null) {
      return translator.t(
        options.seasonWatched ? 'vod.action.markSeasonUnwatched' : 'vod.action.markSeason',
        { season: options.season },
      );
    }
    return translator.t(state.watched ? 'vod.action.markUnwatched' : 'vod.action.markWatched');
  }
  if (item.kind === 'series' && resume !== null) {
    // «Riprendi S2 E3» e non «Riprendi»: il tasto dice **quale** episodio aprirà, perché dopo una
    // pausa di un mese è esattamente la cosa che non si ricorda.
    return translator.t(state.started ? 'vod.action.resumeEpisode' : 'vod.action.startEpisode', {
      season: resume.season,
      episode: resume.episode,
    });
  }
  return translator.t(state.percent > 0 ? 'vod.action.resume' : 'vod.action.watch');
}

function episodeSubtitle(episode: EpisodeInfo, translator: Translator): string {
  if (episode.durationMin !== null) return translator.t('vod.minutes', { count: episode.durationMin });
  return translator.t('vod.season', { number: episode.season });
}
